<script setup lang="ts">
import ShapeDrillHoleGeneratorFactory from '../Models/ShapeDrillHoleGeneratorFactory';

import {computed, onBeforeUnmount, onMounted} from "vue";
import { useStore } from "vuex";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import { stripNonNumeric } from '@/functions/numbers';
import { StepTypes } from '@/types/step';

const store = useStore();

const shape = computed(() => store.getters.shape );
const model = computed(() => store.getters.model );
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration );
const drillHoleEdgeHoles = computed(() => store.getters.drillHoleEdgeHoles );
const drillHoleOffset = computed( () => store.getters.drillHoleOffset );
const isMediaMobileSize = computed( () => store.getters.isMediaMobileSize );
const currentStepName = computed( () => store.getters.currentStepName );

function countEdges() {
	const shapeDrillHoleGenerator = (new ShapeDrillHoleGeneratorFactory()).getShapeDrillHoleGenerator( shape.value, model.value );

	if ( ( 'getPath' in shapeDrillHoleGenerator ) ) {
		return shapeDrillHoleGenerator.getPath( drillHoleOffset.value ).length;
	}

	return 0;
}

const edges = computed(() => {
	const edges = [];
	const sides = countEdges();

	const classes = sides === 4 ? [ 'top', 'right', 'bottom', 'left' ] : [ 'right', 'bottom', 'left' ];

	for( let i = 0; i < sides; i++ ) {
		edges.push( { side: classes[ i ], holes: Math.round( drillHoleEdgeHoles.value[ i ] ?? 1 ) } );
	}

	return edges;
});

document.addEventListener("focusin", focusChanged);

function focusChanged(event) {
	store.commit("updateFocusedElement", event.target);
}

onMounted(async () => {

	// Don't set defaults if we have an old configuration available for edge drill holes.
	if ( lastGeneratedConfiguration.value.drillholes?.type === 'edges' && lastGeneratedConfiguration.value.drillholes?.edgeType === 'around' ) {
		return;
	}

	const defaultHoles = new Array( countEdges() );

	defaultHoles.fill( 1 );

	store.commit('updateEdgeHoles', defaultHoles );
});

onBeforeUnmount(() => {
	store.commit( 'updateEdgeHoles', [] );

	document.removeEventListener("focusin", focusChanged);
});

function updateDrillEdgeHoles(value, index) {
	const data = [...drillHoleEdgeHoles.value]; // Make a shallow copy to prevent modifying the original object. This woudl break reactivity.
	data[index] = stripNonNumeric(value);

	store.commit( 'updateEdgeHoles', data);
}
</script>

<template>
	<div class="grid" v-if="isMediaMobileSize">
		<div v-for="(holeInfo, index) in edges" :key="index" :class="{ [holeInfo.side]: true }">
			<!-- Force this to be text as the arrows make it so that the field doesn't properly fit on mobile. -->
			<dimension-input
				:inputRef='`drillHoleEdgeCount_${index}`'
				:id='`drillHoleEdgeCount_${index}`'
				:name='`drillHoleEdgeCount_${index}`'
				inputmode="numeric"
				type="text"
				:step="1"
				:min="0"
				:modelValue="holeInfo.holes"
				@update:modelValue="updateDrillEdgeHoles($event, index)"
				:invalid="false"
			/>
		</div>

		<div class="center">
			<portal-target name="mobile-accordion-preview" v-if="isMediaMobileSize && currentStepName === StepTypes.drillholes"></portal-target>
		</div>
	</div>
	<dimension-input
		v-else
		v-for="(holeInfo, index) in edges"
		:key="index"
		:inputRef='`drillHoleEdgeCount_${index}`'
		:id='`drillHoleEdgeCount_${index}`'
		:name='`drillHoleEdgeCount_${index}`'
		inputmode="numeric"
		:step="1"
		:min="0"
		:label="`${$i18n.drillHoleConfiguration.edge} ${index +1}`"
		:modelValue="holeInfo.holes"
		@update:modelValue="updateDrillEdgeHoles($event, index)"
		:invalid="false"
	/>
</template>

<style lang="scss">

.center,
.top,
.right,
.bottom,
.left {
	input[type=number],
	input[type=text] {
		text-align: center;
		width: 2rem;
		padding: 2px !important;
	}
}
</style>

<style scoped lang="scss">

.grid {
	display: grid;
	grid-template-columns: 10% minmax(0, 1fr) 10%;
	grid-template-rows: repeat(3, auto);
	grid-template-areas:
		". top ."
		"left center right"
		". bottom .";
	background-color: $brand-blue-background;
	justify-items: center;
	align-items: center;
	width: 100%;
	grid-column: 1/-1;
}

.center,
.top,
.right,
.bottom,
.left {
	display: grid;
	max-width: unset;
	flex-direction: row;
	justify-content: center;
	width: 100%;
}

.top {
	grid-area: top;
}

.right {
	grid-area: right;
}

.bottom {
	grid-area: bottom;
}

.left {
	grid-area: left;
}

.center {
	grid-area: center;
	width: 100%;
}

</style>
